import { ChangeDetectorRef, Directive, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import {
  NotificationRef,
  NotificationService,
} from '@progress/kendo-angular-notification';
import {
  find,
  forEach,
  get,
  has,
  isEqual,
  isNil,
  keys,
  nth,
  reduce,
} from 'lodash';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  take,
  takeUntil,
} from 'rxjs';
import {
  editAction,
  reviewAction,
  reviewassessmentAction,
  viewAction,
  viewassessmentAction,
} from '../../shared/constants/form-controls';
import {
  dialogAnimationDuration,
  dialogTimer,
} from '../constants/dialog-specs';
import { getReferenceDataById } from '../functions/get-reference-data-by-id';
import { IReferenceData } from '../interfaces/form/reference-data.model';
import { ISedmsFormControl } from '../interfaces/form/sedms-form-control.model';
import { ISedmsFormInput } from '../interfaces/form/sedms-form-input.model';
import { ISedmsFormModel } from '../interfaces/form/sedms-form-model';
import { ISedmsValidator } from '../interfaces/form/sedms-validator.model';
import { AbstractComponent } from './abstract-component';
import { CustomErrorComponent } from './custom-error/custom-error.component';

@Directive()
export abstract class AbstractForm<
    T extends ISedmsFormInput,
    S,
    R extends Record<string, IReferenceData[]>
  >
  extends AbstractComponent
  implements OnInit
{
  entity: T;
  referenceData: R;
  protected referenceDataLoaded: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private entityLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  viewMode: boolean = false;
  editMode: boolean = false;
  reviewMode: boolean = false;
  reviewassessmentMode: boolean = false;
  loadSaveSpinner: boolean = false;
  entityData: T; // making  it public to  acces  entity data in  other comopoent
  entityId: string;
  nycId: string;
  viewassessmentMode: boolean;

  constructor(
    protected cdRef: ChangeDetectorRef,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    protected dialogService: DialogService,
    protected notificationService: NotificationService
  ) {
    super();
  }

  // get entity
  abstract get entity$(): Observable<S>;

  // get form
  abstract form: FormGroup;

  // get form model
  abstract formModel: ISedmsFormModel;

  // get referenceData
  abstract get referenceData$(): Observable<IReferenceData[][]>;

  // init entity
  abstract initEntity(data: unknown, referenceData: R): T;

  // init reference data
  abstract initReferenceData(referenceData: IReferenceData[][]): R;

  // initialize
  abstract initWatches(): void;

  // action after cancel confirmation popup
  abstract postCancelConfirmation(): void;

  // save details
  abstract saveDetails(isSubmit?: boolean): Observable<any>;

  // action after submit confirmation popup
  abstract postSubmitConfirmation(isSubmit?: boolean, response?: any): void;

  // can save draft
  abstract canSaveDraft: boolean;

  // can save draft
  abstract canSubmit: boolean;

  ngOnInit(): void {
    combineLatest([this.referenceDataLoaded, this.entityLoaded])
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(isEqual),
        filter(
          ([referenceDataLoaded, entityLoaded]: [boolean, boolean]) =>
            !!referenceDataLoaded && !!entityLoaded
        ),
        take(1)
      )
      .subscribe(([referenceDataLoaded, entityLoaded]: [boolean, boolean]) => {
        if (
          this.viewMode ||
          this.editMode ||
          this.isDialog ||
          this.reviewassessmentMode
        ) {
          this.entity = this.initEntity(this.entityData, this.referenceData);
        }
        this.initForm();
        this.initWatches();
        this.otherInitalizations();
        this.cdRef.detectChanges();
      });

    this.route.paramMap
      .pipe(takeUntil(this.componentDestroyed), distinctUntilChanged(isEqual))
      .subscribe((params: ParamMap) => {
        const action = params.get('action');
        const entityId = params.get('id');
        this.nycId = params.get('nycId');
        if (entityId !== null) {
          this.entityId = entityId;
        }
        if (action === viewAction) {
          this.viewMode = true;
        }
        if (action === editAction) {
          this.editMode = true;
        }
        if (action === reviewAction) {
          this.reviewMode = true;
          this.viewMode = true;
        }
        if (action === reviewassessmentAction) {
          this.reviewassessmentMode = true;
          this.viewMode = true;
        }
        if (action === viewassessmentAction) {
          this.viewassessmentMode = true;
          this.viewMode = true;
        }
        this.loadReferenceData();
        this.loadEntity(!this.viewMode && !this.editMode && !this.isDialog);
      });
  }

  cancel(customMessage: string = null): void {
    this.postCancelConfirmation();
    // if (this.viewMode) {
    //     this.postCancelConfirmation();
    // } else {
    //     const dialog: DialogRef = this.dialogService.open({
    //         title: 'Please confirm',
    //         content: customMessage ? customMessage : 'Are you sure you want to cancel?',
    //         actions: [{ text: 'No' }, { text: 'Yes', themeColor: 'primary' }],
    //         width: dialogWidth,
    //         height: dialogHeight,
    //         minWidth: dialogMinWidth,
    //     });

    //     dialog.result.pipe(takeUntil(this.componentDestroyed)).subscribe((result) => {
    //         if (result instanceof DialogCloseResult) {
    //             //
    //         } else {
    //             if (result?.text === 'Yes') {
    //                 this.postCancelConfirmation();
    //             }
    //         }
    //     });
    // }
  }

  preSubmit(): boolean {
    return true;
  }

  get isDialog(): boolean {
    return false;
  }

  submit(defaultSuccessMsg: boolean = true): void {
    this.save(true, defaultSuccessMsg);
  }

  save(isSubmit: boolean = false, defaultSuccessMsg: boolean = true): void {
    if (this.preSubmit()) {
      this.loadSaveSpinner = true;
      this.saveDetails(isSubmit)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe({
          next: (response: any) => {
            this.loadSaveSpinner = false;
            if (response?.isRequestSucessful === false) {
              const notificationRef: NotificationRef =
                this.notificationService.show({
                  content: CustomErrorComponent,
                  cssClass: 'notification-error',
                  position: { horizontal: 'center', vertical: 'top' },
                  animation: { type: 'slide', duration: 60 },
                  type: { style: 'error', icon: false },
                  closable: true,
                });
              const notificationInstance = notificationRef.content.instance;
              notificationInstance.data = response;
              // this.loadSaveSpinner = false;
            } else {
              if (defaultSuccessMsg) {
                this.notificationService.show({
                  content: isSubmit
                    ? 'Submitted Successfully'
                    : 'Saved Successfully',
                  cssClass: 'notification-success',
                  hideAfter: dialogTimer,
                  position: { horizontal: 'center', vertical: 'top' },
                  animation: {
                    type: 'slide',
                    duration: dialogAnimationDuration,
                  },
                  type: { style: 'success', icon: true },
                });
              }
              // this.loadSaveSpinner = false;
              this.postSubmitConfirmation(isSubmit, response);
            }
          },
          error: (error) => {
            this.notificationService.show({
              content:
                'Submission Failed' +
                (error?.error?.error ? ': ' + error.error.error : ''),
              cssClass: 'notification-error',
              hideAfter: dialogTimer,
              position: { horizontal: 'center', vertical: 'top' },
              animation: { type: 'slide', duration: dialogAnimationDuration },
              type: { style: 'error', icon: true },
            });
            this.loadSaveSpinner = false;
          },
        });
    }
    this.cdRef.detectChanges();
  }

  afterOnDestroy(): void {
    // please override this method if needed
  }

  reinitializeWithNewEntity() {
    this.entityLoaded.next(false);
    combineLatest([this.entityLoaded])
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(isEqual),
        filter(([entityLoaded]: [boolean]) => !!entityLoaded),
        take(1)
      )
      .subscribe(([entityLoaded]: [boolean]) => {
        if (
          this.viewMode ||
          this.editMode ||
          this.isDialog ||
          this.reviewassessmentMode
        ) {
          this.entity = this.initEntity(this.entityData, this.referenceData);
        }
        this.initForm();
        this.initWatches();
        this.otherInitalizations();
        this.cdRef.detectChanges();
      });
  }

  protected otherInitalizations(): void {
    // please override this method if needed
  }

  private loadReferenceData(): void {
    this.referenceData$
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(isEqual),
        filter((referenceData: IReferenceData[][]) => !!referenceData)
      )
      .subscribe((referenceData: IReferenceData[][]) => {
        this.referenceData = this.initReferenceData(referenceData);
        this.referenceDataLoaded.next(true);
      });
  }

  private loadEntity(addMode: boolean): void {
    if (addMode) {
      this.entityLoaded.next(true);
    } else {
      this.entity$
        .pipe(
          takeUntil(this.componentDestroyed),
          distinctUntilChanged(isEqual),
          filter((data: T) => !!data && !isNil(data))
        )
        .subscribe((data: T) => {
          this.entityData = data;
          this.entityLoaded.next(true);
        });
    }
  }

  private initForm(): void {
    this.form = this.formBuilder.group(this.dynamicFormGroup());
  }

  private createFormControl(
    ctrl: ISedmsFormModel | ISedmsFormControl | ISedmsFormControl[],
    key: string,
    index?: number
  ): FormGroup | FormControl | FormArray {
    if (Array.isArray(ctrl)) {
      const frmArray: FormArray = this.formBuilder.array([]);
      forEach(get(this.entity, key), (val: string, i: number) => {
        frmArray.push(this.dynamicFormArray(ctrl, key, i));
      });
      return frmArray;
    } else if (has(ctrl, 'name')) {
      // If ctrl is an instance of ISedmsFormControl
      const validator: ValidatorFn | null = this.createValidators(
        ctrl as ISedmsFormControl
      );
      return new FormControl(
        {
          value: this.getFormControlInitialValue(
            ctrl as ISedmsFormControl,
            key,
            index
          ),
          disabled: this.viewMode || this.reviewMode ? true : ctrl.disabled,
        },
        validator
      );
    } else {
      return new FormGroup(this.dynamicFormGroup(ctrl as ISedmsFormModel, key));
    }
  }

  private getFormControlInitialValue(
    ctrl: ISedmsFormControl,
    key: string,
    index?: number
  ) {
    if (!this.editMode && this.viewMode) {
      return this.getControlValue(ctrl, key, index);
    }

    const ctrlValue: any = this.getControlValue(ctrl, key, index);

    // data exists, return it
    if (ctrlValue != null) {
      return ctrlValue;
    }

    // data does not exist, use default
    let ctrlDefault: any = ctrl.default;

    // if reference data name is passed in, try to get the value and use it
    if (ctrl.referenceData) {
      const ctrlReferenceData: IReferenceData[] =
        this.referenceData[ctrl.referenceData] || [];
      ctrlDefault = getReferenceDataById(ctrl.default, ctrlReferenceData);
    }

    return ctrlValue || ctrlDefault || null;
  }

  private getControlValue(
    ctrl: ISedmsFormControl,
    key: string,
    index?: number
  ) {
    if (ctrl.name !== key) {
      if (!isNil(index)) {
        return get(nth(get(this.entity, key), index), ctrl.name) || null;
      } else {
        const value = get(get(this.entity, key), ctrl.name);
        if (value !== null && value != undefined) {
          return value;
        }
        return null;
      }
    } else {
      return get(this.entity, ctrl.name) || null;
    }
  }

  private createValidators(config: ISedmsFormControl): ValidatorFn | null {
    let validator: ValidatorFn | null = null;
    if (config.required) {
      validator = Validators.required;
    }
    if (config.max || config.max === 0) {
      validator = Validators.compose([validator, Validators.max(config.max)]);
    }
    if (config.min || config.min === 0) {
      validator = Validators.compose([validator, Validators.min(config.min)]);
    }
    if (config.maxLength || config.maxLength === 0) {
      validator = Validators.compose([
        validator,
        Validators.maxLength(config.maxLength),
      ]);
    }
    if (config.minLength || config.minLength === 0) {
      validator = Validators.compose([
        validator,
        Validators.minLength(config.minLength),
      ]);
    }
    if (config.pattern) {
      validator = Validators.compose([
        validator,
        Validators.pattern(config.pattern),
      ]);
    }
    // Add provided custom validators to the validator function
    if (config.validators) {
      config.validators.forEach((validatorConfig: ISedmsValidator) => {
        validator = Validators.compose([validator, validatorConfig.validator]);
      });
    }
    return validator;
  }

  private dynamicFormGroup(
    formModel?: ISedmsFormModel,
    entitySection?: string
  ): Record<string, FormGroup | FormControl | FormArray> {
    return reduce(
      keys(formModel ?? this.formModel),
      (
        acc: Record<string, FormGroup | FormControl | FormArray>,
        key: string
      ) => {
        return {
          ...acc,
          [key]: formModel
            ? this.createFormControl(
                get(formModel, key),
                /*!Array.isArray(get(formModel, key)) && */ !get(
                  get(formModel, key),
                  'name'
                )
                  ? `${entitySection}.${key}`
                  : entitySection
              )
            : this.createFormControl(get(this.formModel, key), key),
        };
      },
      {}
    );
  }

  private dynamicFormArray(
    controls: ISedmsFormControl[],
    key: string,
    index: number
  ): FormGroup {
    return this.formBuilder.group(
      reduce(
        controls,
        (acc: Record<string, FormControl>, ctrl: ISedmsFormControl) => {
          return {
            ...acc,
            [ctrl.name]: this.createFormControl(
              ctrl,
              key,
              index
            ) as FormControl,
          };
        },
        {}
      )
    );
  }

  public dynamicFormArrayGroup(groupName: string): FormGroup {
    return this.formBuilder.group(
      reduce(
        get(this.formModel, groupName),
        (
          acc: Record<string, FormGroup | FormControl | FormArray>,
          key: ISedmsFormControl
        ) => {
          return {
            ...acc,
            [key.name]: this.createFormControl(
              find(
                get(this.formModel, groupName),
                (ctrl: ISedmsFormControl) => ctrl?.name === key.name
              ) as ISedmsFormControl,
              key.name
            ),
          };
        },
        {}
      )
    );
  }
}
